// src/stores/accessControl.ts
import { defineStore } from 'pinia'
import api from "@/services/api";
import type { PrincipalType, LookupOption } from '@bono-packages/access-control'

// Interfaces for access control.
export interface AccessRule {
  principalType: PrincipalType;
  principalValue: string;
  allowedRoles: string[];
  description: string;
}

export interface AccessControlModel {
  entity: string;
  // Each key is an application name (e.g., INTERNAL, CustomerApp1, etc.)
  // and its value is an array of access rules.
  applications: Record<string, AccessRule[]>;
}

export const useEntityAccessControlStore = defineStore('accessControl', {
  state: () => ({
    // Array of applications served from the API.
    apps: [] as LookupOption[],
    // Cache for principal value options: { [app]: { [principalType]: LookupOption[] } }
    principalValueOptions: {} as Record<string, Record<PrincipalType, LookupOption[]>>,
    // Cache for role options per app: { [app]: LookupOption[] }
    roleOptions: {} as Record<string, LookupOption[]>,
    // You can also store the access control model itself in the store.
    accessControlModel: {
      entity: '',
      applications: {}
    } as AccessControlModel
  }),
  actions: {
    // Fetch the list of applications from the API.
    async fetchApps() {
      try {
        const { data } = await api.get<LookupOption[]>('access-control/apps')
        this.apps = data
      } catch (error) {
        console.error('Error fetching apps', error)
      }
    },

    // Fetch principal values for a specific app and principal type.
    async fetchPrincipalValues(app: string, principalType: PrincipalType): Promise<LookupOption[]> {
      try {
        const { data } = await api.get<LookupOption[]>(`access-control/principal-values/${localStorage.getItem('companyId')}`, {
          params: { app, principalType }
        })
        // Cache the lookup options.
        if (!this.principalValueOptions[app]) {
          this.principalValueOptions[app] = {} as any
        }
        this.principalValueOptions[app][principalType] = data
        return data
      } catch (error) {
        console.error(`Error fetching principal values for app ${app} and type ${principalType}`, error)
        return []
      }
    },

    // Fetch role options for a specific app.
    async fetchRoleOptions(app: string): Promise<LookupOption[]> {
      try {
        const { data } = await api.get<LookupOption[]>('access-control/roles', {
          params: { app }
        })
        this.roleOptions[app] = data
        return data
      } catch (error) {
        console.error(`Error fetching role options for app ${app}`, error)
        return []
      }
    },

    // Get cached principal values; if not present, returns an empty array.
    getPrincipalValues(app: string, principalType: string): LookupOption[] {
      return this.principalValueOptions[app]?.[principalType as PrincipalType] || []
    },

    // Get cached role options for an app.
    getRoleOptions(app: string): LookupOption[] {
      return this.roleOptions[app] || []
    },

    // Set or update the access control model.
    setAccessControlModel(model: AccessControlModel) {
      this.accessControlModel = model
    },

    // Update a specific application's access rules in the model.
    updateAppRules(app: string, rules: AccessRule[]) {
      this.accessControlModel.applications[app] = rules
    }
  }
})
